import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Download, Smartphone, Monitor, Share, Plus, Check, Sparkles, ShieldCheck, WifiOff, Bell, AlertTriangle, ExternalLink, Copy } from "lucide-react";
import { track } from "@/lib/analytics";
import { seoMeta } from "@/lib/schema";

const DOWNLOAD_TITLE = "Install KAfrequency — Direct from Buddha KA";
const DOWNLOAD_DESCRIPTION =
  "Install KAfrequency directly from buddhaka.com. iPhone, iPad, Android, and desktop. No app-store account required. First-party, signed, and owned by the artist.";

export const Route = createFileRoute("/download")({
  head: () => {
    const seo = seoMeta({
      title: DOWNLOAD_TITLE,
      description: DOWNLOAD_DESCRIPTION,
      path: "/download",
    });
    return {
      ...seo,
      meta: [...seo.meta, { name: "theme-color", content: "#080808" }],
    };
  },

  component: DownloadPage,
});

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

type Device = "ios-phone" | "ios-pad" | "android-phone" | "android-tablet" | "windows" | "macos" | "linux" | "unknown";
type Browser = "safari" | "chrome" | "edge" | "firefox" | "brave" | "arc" | "samsung" | "in-app" | "other";

interface AndroidRelease {
  available: boolean;
  version_name?: string;
  version_code?: number;
  min_os_version?: string;
  file_size_bytes?: number;
  sha256?: string;
  signing_fingerprint?: string;
  release_notes?: string;
  mandatory?: boolean;
  published_at?: string;
  download_url?: string;
}

function detectDevice(): Device {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  const plat = navigator.platform || "";
  const touch = "ontouchend" in document;
  if (/iPhone|iPod/.test(ua)) return "ios-phone";
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && touch)) return "ios-pad";
  if (/Android/i.test(ua)) return /Mobile/.test(ua) ? "android-phone" : "android-tablet";
  if (/Win/.test(plat)) return "windows";
  if (/Mac/.test(plat)) return "macos";
  if (/Linux/.test(plat)) return "linux";
  return "unknown";
}

function detectBrowser(): Browser {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent;
  // In-app browsers first
  if (/FBAN|FBAV|Instagram|Line|Twitter|TikTok|Snapchat|WhatsApp|MicroMessenger/i.test(ua)) return "in-app";
  if (/Edg\//.test(ua)) return "edge";
  if (/SamsungBrowser/.test(ua)) return "samsung";
  if (/Brave/.test(ua) || (navigator as unknown as { brave?: unknown }).brave) return "brave";
  if (/Arc\//.test(ua)) return "arc";
  if (/Firefox/.test(ua)) return "firefox";
  if (/CriOS|Chrome/.test(ua)) return "chrome";
  if (/Safari/.test(ua)) return "safari";
  return "other";
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function fmtBytes(b?: number) {
  if (!b) return "—";
  const mb = b / (1024 * 1024);
  return `${mb.toFixed(mb >= 100 ? 0 : 1)} MB`;
}

function DownloadPage() {
  const [device, setDevice] = useState<Device>("unknown");
  const [browser, setBrowser] = useState<Browser>("other");
  const [installed, setInstalled] = useState(false);
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [android, setAndroid] = useState<AndroidRelease | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const isIOS = device === "ios-phone" || device === "ios-pad";
  const isAndroid = device === "android-phone" || device === "android-tablet";
  const isDesktop = device === "windows" || device === "macos" || device === "linux";

  const qrUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const href = `${window.location.origin}/download`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=240x240&bgcolor=080808&color=f5d67a&margin=8&data=${encodeURIComponent(href)}`;
  }, []);

  useEffect(() => {
    const d = detectDevice();
    const b = detectBrowser();
    setDevice(d);
    setBrowser(b);
    setInstalled(isStandalone());
    try {
      track("download_page_view");
      track("download_platform_detected", { device: d, browser: b });
    } catch {}

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // Fetch Android release manifest
    fetch("/api/public/releases/android/latest")
      .then((r) => (r.ok ? r.json() : { available: false }))
      .then((data: AndroidRelease) => setAndroid(data))
      .catch(() => setAndroid({ available: false }));

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  async function handlePwaInstall() {
    if (!deferred) return;
    try { track("pwa_install_prompt_shown", { source: "download_page" }); } catch {}
    await deferred.prompt();
    const choice = await deferred.userChoice;
    try { track("pwa_install_choice", { outcome: choice.outcome, source: "download_page" }); } catch {}
    setDeferred(null);
  }

  function handleApkDownload() {
    try { track("android_apk_download_started", { version: android?.version_name }); } catch {}
  }

  function copyChecksum() {
    if (!android?.sha256) return;
    navigator.clipboard.writeText(android.sha256).then(() => {
      setCopied("sha");
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div className="mx-auto max-w-5xl px-4 pb-24 pt-10 sm:px-6 sm:pt-16">
      {/* Hero */}
      <header className="text-center">
        <p className="text-xs uppercase tracking-[0.35em] text-gold">Sovereign Distribution · Direct from the Source</p>
        <h1 className="mt-4 font-serif text-4xl leading-tight text-gold-luminous sm:text-6xl">
          Install KAfrequency.
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-sm text-muted-foreground sm:text-base">
          Installed directly from <span className="text-gold-luminous">buddhaka.com</span>. No app-store account required.
          One account. Every device. Owned end-to-end by Buddha KA.
        </p>
      </header>

      {installed && (
        <div className="mt-8 mx-auto max-w-md rounded-2xl border border-gold/40 bg-gold/5 p-4 text-center">
          <div className="flex items-center justify-center gap-2 text-gold-luminous">
            <Check size={16} /> <span className="text-sm font-medium">KAfrequency is installed on this device.</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Launch from your home screen or dock for the full experience.</p>
        </div>
      )}

      {/* In-app browser warning (mobile share sheets) */}
      {browser === "in-app" && !installed && (
        <div className="mt-8 mx-auto max-w-2xl rounded-2xl border border-amber-500/50 bg-amber-500/10 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-400" />
            <div>
              <p className="text-sm font-medium text-amber-100">You&apos;re inside an in-app browser.</p>
              <p className="mt-1 text-xs text-amber-200/80">
                Installing to your home screen requires opening buddhaka.com in{" "}
                <strong>{isIOS ? "Safari" : "Chrome"}</strong> directly. Tap the menu (⋯ or ⋮) and choose{" "}
                <em>Open in browser</em>, then return to this page.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Primary path — device-detected */}
      <section className="mt-10 sm:mt-14">
        {isIOS && !installed && <IOSPath device={device} browser={browser} />}
        {isAndroid && !installed && (
          <AndroidPath
            release={android}
            deferred={deferred}
            onPwaInstall={handlePwaInstall}
            onApkDownload={handleApkDownload}
            copyChecksum={copyChecksum}
            copied={copied === "sha"}
          />
        )}
        {isDesktop && !installed && (
          <DesktopPath deferred={deferred} onInstall={handlePwaInstall} qrUrl={qrUrl} browser={browser} />
        )}
        {device === "unknown" && !installed && (
          <UnknownPath deferred={deferred} onInstall={handlePwaInstall} qrUrl={qrUrl} />
        )}
      </section>

      {/* Secondary — all methods */}
      <section className="mt-16">
        <h2 className="font-serif text-2xl text-gold-luminous sm:text-3xl">All installation methods</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <MethodCard icon={<Smartphone size={16} />} title="iPhone & iPad" body="Safari → Share → Add to Home Screen. Installs as a full-screen app." />
          <MethodCard icon={<Smartphone size={16} />} title="Android" body="Direct signed APK download, or install the web app from Chrome." />
          <MethodCard icon={<Monitor size={16} />} title="Desktop" body="Chrome, Edge, Brave, Arc — one-click install to your dock or taskbar." />
        </div>
      </section>

      {/* Benefits */}
      <section className="mt-16">
        <h2 className="font-serif text-2xl text-gold-luminous sm:text-3xl">Why install</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Benefit icon={<WifiOff size={16} />} title="Works offline" body="Downloaded activations, music, and readings stay available without signal." />
          <Benefit icon={<Bell size={16} />} title="Daily activations" body="Rising, midday, evening, and night seal — as push notifications you control." />
          <Benefit icon={<ShieldCheck size={16} />} title="First-party & signed" body="Served directly from buddhaka.com. Android APKs are signed with a permanent key you can verify." />
          <Benefit icon={<Sparkles size={16} />} title="Owned by the artist" body="No app-store gatekeepers. Your account, your content, one place." />
        </div>
      </section>

      {/* Android verification / release info */}
      {android?.available && (
        <section className="mt-16">
          <h2 className="font-serif text-2xl text-gold-luminous sm:text-3xl">Android release — verification</h2>
          <div className="mt-6 rounded-2xl border hairline p-5">
            <dl className="grid gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
              <Info label="Version" value={android.version_name} />
              <Info label="Version code" value={android.version_code?.toString()} />
              <Info label="Minimum Android" value={android.min_os_version ?? "8.0"} />
              <Info label="File size" value={fmtBytes(android.file_size_bytes)} />
              <Info label="Published" value={android.published_at ? new Date(android.published_at).toLocaleDateString() : "—"} />
              <Info label="Channel" value={android.mandatory ? "Critical" : "Stable"} />
              {android.signing_fingerprint && (
                <div className="sm:col-span-2">
                  <dt className="text-xs uppercase tracking-widest text-muted-foreground">Signing fingerprint (SHA-256)</dt>
                  <dd className="mt-1 break-all font-mono text-xs text-ivory">{android.signing_fingerprint}</dd>
                </div>
              )}
              {android.sha256 && (
                <div className="sm:col-span-2">
                  <dt className="text-xs uppercase tracking-widest text-muted-foreground">APK checksum (SHA-256)</dt>
                  <dd className="mt-1 flex items-start gap-2 break-all font-mono text-xs text-ivory">
                    <span className="flex-1">{android.sha256}</span>
                    <button
                      type="button"
                      onClick={copyChecksum}
                      className="shrink-0 rounded-md border hairline p-1 text-muted-foreground hover:border-gold hover:text-gold"
                      aria-label="Copy checksum"
                    >
                      {copied === "sha" ? <Check size={12} /> : <Copy size={12} />}
                    </button>
                  </dd>
                </div>
              )}
            </dl>
            {android.release_notes && (
              <div className="mt-5 border-t hairline pt-4">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Release notes</p>
                <p className="mt-2 whitespace-pre-wrap text-sm text-ivory">{android.release_notes}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="mt-16">
        <h2 className="font-serif text-2xl text-gold-luminous sm:text-3xl">Frequently asked</h2>
        <div className="mt-6 space-y-3">
          <Faq q="Why isn't this in the App Store or Google Play?">
            KAfrequency is distributed directly from buddhaka.com so the artist retains full ownership of the platform, the content, the payments, and the customer relationship. No third-party gatekeeper. No 30% cut. No arbitrary removals.
          </Faq>
          <Faq q="Is it safe to install directly from your website?">
            Yes. buddhaka.com is served over HTTPS. The Android APK is signed with a permanent KAfrequency signing key — you can verify the SHA-256 checksum and the signing certificate fingerprint above before installing. The signature never changes across updates, so Android will reject any tampered copy.
          </Faq>
          <Faq q="Will Android warn me during install?">
            The first time, yes — Android requires you to allow installation from your browser for any app that isn&apos;t from Google Play. That&apos;s a standard, one-time permission you can turn back off after installing. It doesn&apos;t mean the app is unsafe; it just means we chose sovereign distribution.
          </Faq>
          <Faq q="Does it work offline?">
            Yes. The app shell, previously visited pages, and any downloaded media stay available offline. Purchases and journal entries queue and sync when you reconnect.
          </Faq>
          <Faq q="Will my account sync across devices?">
            Your account, purchases, subscriptions, streak, and activation history sync across every installed device the moment you sign in.
          </Faq>
          <Faq q="How do I update?">
            The web app self-updates in the background — you&apos;ll see a subtle &quot;Reload to update&quot; notice when a new version is available. The Android app checks buddhaka.com for updates and notifies you in-app; you&apos;re always in control of installing.
          </Faq>
          <Faq q="How do I uninstall?">
            iPhone/iPad: long-press the icon → Delete App. Android: Settings → Apps → KAfrequency → Uninstall. Desktop: browser menu → Uninstall KAfrequency.
          </Faq>
          <Faq q="Something looks stale.">
            Visit <a className="text-gold underline" href="/?sw=off">/?sw=off</a> once to clear the offline cache, then reload.
          </Faq>
        </div>
      </section>

      {/* Ownership statement */}
      <section className="mt-16 rounded-2xl border border-gold/30 bg-gold/5 p-6 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Sovereign · First-party · Signed</p>
        <p className="mx-auto mt-3 max-w-xl font-serif text-lg text-gold-luminous">
          Install KAfrequency directly from the source. No app-store account required.
        </p>
        <p className="mt-3 text-xs text-muted-foreground">
          KAfrequency is owned and operated by Buddha KA. Your data, your account, your subscription — all administered on buddhaka.com.
        </p>
      </section>

      {/* Legal */}
      <section className="mt-16 border-t hairline pt-8 text-center text-xs text-muted-foreground">
        <p>
          By installing you agree to the{" "}
          <a href="/terms" className="text-gold underline">Terms</a> and{" "}
          <a href="/privacy" className="text-gold underline">Privacy Policy</a>.
        </p>
        <p className="mt-2">
          Need help? <a href="/contact" className="text-gold underline">Contact support</a>.
        </p>
      </section>
    </div>
  );
}

/* ------------ Path components ------------- */

function IOSPath({ device, browser }: { device: Device; browser: Browser }) {
  const isSafari = browser === "safari";
  return (
    <div className="rounded-3xl border border-gold/50 bg-gold/5 p-6 sm:p-8 shadow-halo">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gold-gradient text-obsidian"><Smartphone size={18} /></div>
        <div>
          <p className="text-xs uppercase tracking-widest text-gold">Detected · {device === "ios-pad" ? "iPad" : "iPhone"}</p>
          <h2 className="font-serif text-2xl text-gold-luminous">Add KAfrequency to your Home Screen</h2>
        </div>
      </div>
      {!isSafari && (
        <div className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-100">
          For the best installation experience, open <span className="font-mono text-amber-50">buddhaka.com/download</span> in <strong>Safari</strong>. Some other browsers on iOS don&apos;t support Add to Home Screen.
        </div>
      )}
      <ol className="mt-6 space-y-4">
        <IOSStep n={1} icon={<Share size={16} />} title="Tap the Share button">
          Look for the <Share size={12} className="inline align-middle text-gold" /> icon in Safari&apos;s toolbar — at the bottom on iPhone, at the top on iPad.
        </IOSStep>
        <IOSStep n={2} icon={<Plus size={16} />} title="Choose Add to Home Screen">
          Scroll down in the share sheet and tap <em>Add to Home Screen</em>.
        </IOSStep>
        <IOSStep n={3} icon={<Check size={16} />} title="Confirm and Add">
          The name will read <span className="text-gold-luminous">KAfrequency</span> with the official icon. Tap <em>Add</em>.
        </IOSStep>
        <IOSStep n={4} icon={<Sparkles size={16} />} title="Launch from your Home Screen">
          Return to your Home Screen. Tap the KAfrequency icon. The app opens full-screen — no browser bar.
        </IOSStep>
        <IOSStep n={5} icon={<Bell size={16} />} title="Enable notifications (inside the app)">
          Open Settings → Notifications and turn on the activations you want. iOS asks for permission the first time.
        </IOSStep>
      </ol>
    </div>
  );
}

function IOSStep({ n, icon, title, children }: { n: number; icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-4">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gold/50 bg-obsidian text-xs font-serif text-gold-luminous">{n}</div>
      <div>
        <p className="flex items-center gap-2 text-sm font-medium text-ivory"><span className="text-gold">{icon}</span>{title}</p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{children}</p>
      </div>
    </li>
  );
}

function AndroidPath({
  release, deferred, onPwaInstall, onApkDownload, copyChecksum, copied,
}: {
  release: AndroidRelease | null;
  deferred: BeforeInstallPromptEvent | null;
  onPwaInstall: () => void;
  onApkDownload: () => void;
  copyChecksum: () => void;
  copied: boolean;
}) {
  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* Primary — APK */}
      <div className="rounded-3xl border border-gold/50 bg-gold/5 p-6 sm:p-8 shadow-halo">
        <p className="text-xs uppercase tracking-widest text-gold">Recommended · Android</p>
        <h2 className="mt-2 font-serif text-2xl text-gold-luminous">Download the signed app</h2>
        <p className="mt-2 text-xs text-muted-foreground">
          Direct signed APK from buddhaka.com. Full push notifications, deep links, and offline support.
        </p>
        {release?.available ? (
          <>
            <a
              href="/api/public/releases/android/download"
              onClick={onApkDownload}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian shadow-gold hover:shadow-halo"
            >
              <Download size={16} /> Download KAfrequency for Android
            </a>
            <p className="mt-3 text-[11px] text-muted-foreground">
              v{release.version_name} · {fmtBytes(release.file_size_bytes)} · Android {release.min_os_version ?? "8.0"}+
            </p>
            {release.sha256 && (
              <button
                type="button"
                onClick={copyChecksum}
                className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-gold"
              >
                {copied ? <Check size={11} /> : <Copy size={11} />}
                {copied ? "Checksum copied" : "Copy SHA-256 checksum"}
              </button>
            )}
            <AndroidInstallGuide />
          </>
        ) : (
          <div className="mt-5 rounded-xl border hairline bg-obsidian/40 p-4 text-xs text-muted-foreground">
            The signed Android build isn&apos;t published yet. Install the web app below for now — you&apos;ll be notified when the native release is available.
          </div>
        )}
      </div>

      {/* Secondary — PWA */}
      <div className="rounded-3xl border hairline bg-obsidian/40 p-6 sm:p-8">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Alternative · No download</p>
        <h2 className="mt-2 font-serif text-2xl text-gold-luminous">Install the web app</h2>
        <p className="mt-2 text-xs text-muted-foreground">
          Installs from Chrome without downloading a file. Great for a quick start.
        </p>
        {deferred ? (
          <button
            type="button"
            onClick={onPwaInstall}
            className="mt-5 inline-flex items-center gap-2 rounded-full border border-gold px-5 py-2.5 text-sm font-medium text-gold-luminous hover:bg-gold hover:text-obsidian"
          >
            <Download size={16} /> Install web app
          </button>
        ) : (
          <ol className="mt-5 space-y-2 text-xs text-muted-foreground">
            <li>Open this page in <strong>Chrome</strong>.</li>
            <li>Tap ⋮ → <em>Install app</em> (or <em>Add to Home screen</em>).</li>
            <li>Confirm. Launch from your app drawer.</li>
          </ol>
        )}
      </div>
    </div>
  );
}

function AndroidInstallGuide() {
  return (
    <details className="mt-5 rounded-xl border hairline bg-obsidian/40 p-4 open:border-gold/40">
      <summary className="cursor-pointer list-none text-xs font-medium text-ivory">
        <span className="mr-2 text-gold">›</span>What to expect during install
      </summary>
      <ol className="mt-3 space-y-2 text-xs leading-relaxed text-muted-foreground">
        <li><strong className="text-ivory">1.</strong> The APK downloads to your device. Tap it when the download finishes.</li>
        <li><strong className="text-ivory">2.</strong> Android may ask: <em>&quot;Allow this browser to install apps?&quot;</em> Turn it on for your browser only.</li>
        <li><strong className="text-ivory">3.</strong> Confirm the install. The signing fingerprint is verified against KAfrequency&apos;s permanent key.</li>
        <li><strong className="text-ivory">4.</strong> Open KAfrequency from your app drawer.</li>
        <li><strong className="text-ivory">5.</strong> (Optional) Return to Settings and turn off &quot;install unknown apps&quot; for the browser to keep your device tight.</li>
      </ol>
      <p className="mt-3 text-[11px] text-muted-foreground/80">
        This is a normal Android permission, not a warning that the app is unsafe. It appears for every app installed outside Google Play. The APK is signed — Android verifies it before installing, and every future update must be signed with the exact same key or Android will refuse the update.
      </p>
    </details>
  );
}

function DesktopPath({
  deferred, onInstall, qrUrl, browser,
}: {
  deferred: BeforeInstallPromptEvent | null;
  onInstall: () => void;
  qrUrl: string;
  browser: Browser;
}) {
  const supportsPwa = browser === "chrome" || browser === "edge" || browser === "brave" || browser === "arc";
  return (
    <div className="grid gap-6 md:grid-cols-[1.5fr_1fr]">
      <div className="rounded-3xl border border-gold/50 bg-gold/5 p-6 sm:p-8 shadow-halo">
        <p className="text-xs uppercase tracking-widest text-gold">Detected · Desktop</p>
        <h2 className="mt-2 font-serif text-2xl text-gold-luminous">Install as a desktop app</h2>
        <p className="mt-2 text-xs text-muted-foreground">
          Launches like a native app with its own icon in your dock or taskbar.
        </p>
        {deferred ? (
          <button
            type="button"
            onClick={onInstall}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian shadow-gold hover:shadow-halo"
          >
            <Download size={16} /> Install KAfrequency
          </button>
        ) : supportsPwa ? (
          <ol className="mt-5 space-y-2 text-xs text-muted-foreground">
            <li>Look for the <span className="rounded border hairline px-1.5 py-0.5 text-[10px] text-gold">Install</span> icon in the address bar.</li>
            <li>Or open the browser menu → <em>Install KAfrequency…</em></li>
            <li>Confirm. It launches as a standalone window.</li>
          </ol>
        ) : (
          <div className="mt-5 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-100">
            Your browser doesn&apos;t currently support desktop install. Open buddhaka.com in <strong>Chrome</strong>, <strong>Edge</strong>, <strong>Brave</strong>, or <strong>Arc</strong> to install.
          </div>
        )}
      </div>
      <div className="rounded-3xl border hairline bg-obsidian/40 p-6 sm:p-8">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Or install on mobile</p>
        <p className="mt-2 text-xs text-muted-foreground">Scan with your phone to install on iOS or Android.</p>
        {qrUrl && (
          <img src={qrUrl} alt="QR code to open buddhaka.com/download on mobile" width={220} height={220} className="mt-4 w-full max-w-[220px] rounded-xl border hairline" />
        )}
      </div>
    </div>
  );
}

function UnknownPath({ deferred, onInstall, qrUrl }: { deferred: BeforeInstallPromptEvent | null; onInstall: () => void; qrUrl: string }) {
  return (
    <div className="rounded-3xl border hairline p-6 sm:p-8">
      <h2 className="font-serif text-2xl text-gold-luminous">Choose your device</h2>
      <p className="mt-2 text-sm text-muted-foreground">We couldn&apos;t detect your device. Pick an option:</p>
      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <a href="#ios" className="rounded-xl border hairline p-4 text-sm hover:border-gold">iPhone / iPad →</a>
        <a href="#android" className="rounded-xl border hairline p-4 text-sm hover:border-gold">Android →</a>
        <a href="#desktop" className="rounded-xl border hairline p-4 text-sm hover:border-gold">Desktop →</a>
      </div>
      {deferred && (
        <button
          type="button"
          onClick={onInstall}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian"
        >
          <Download size={16} /> Install KAfrequency
        </button>
      )}
      {qrUrl && (
        <img src={qrUrl} alt="QR code" width={180} height={180} className="mt-6 rounded-xl border hairline" />
      )}
    </div>
  );
}

/* ------------ Bits ------------- */

function MethodCard({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="rounded-2xl border hairline p-4">
      <div className="flex items-center gap-2 text-gold">{icon}<span className="text-sm font-medium text-ivory">{title}</span></div>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}

function Benefit({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="rounded-2xl border hairline p-4">
      <div className="flex items-center gap-2 text-gold">{icon}<span className="text-sm font-medium text-ivory">{title}</span></div>
      <p className="mt-2 text-xs text-muted-foreground">{body}</p>
    </div>
  );
}

function Faq({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <details className="group rounded-xl border hairline p-4 open:border-gold/40">
      <summary className="cursor-pointer list-none text-sm font-medium text-ivory">
        <span className="mr-2 text-gold">›</span>{q}
      </summary>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{children}</p>
    </details>
  );
}

function Info({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-widest text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm text-ivory">{value ?? "—"}</dd>
    </div>
  );
}

// Silence unused import warning for ExternalLink (kept for future "Open App" action).
void ExternalLink;
